import moment from "moment-timezone";

import { ErrorFlagger } from "./validate";

type Types = {
	string: string;
	url: string;
	integerstring: string;
	floatstring: string;
	number: number;
	datestring: string;
	completedatefieldselection: { year: number; month: number; date: number };
	boolean: boolean;
	"1": "1";
	date: Date;
	function: Function;
	enum: string | number;
	absent: unknown;
	keyvalue: Record<string, unknown>;
	keyvaluewithboolean: Record<string, unknown>;
	array: unknown[];
};

type Field = {
	type: keyof Types;
	isRequired?: boolean;
	canBeNull?: boolean;
	allowedValues?: (string | number)[];
	followUpChecks?: ({ findInvalid, value }: FollowUpChecksParams) => void;
};

export type FollowUpChecksParams<T extends keyof Types | unknown = unknown> = {
	findInvalid: (errMsg: string) => void;
	value: T extends keyof Types ? Types[T] : unknown;
};

export type FieldChecker<T> = (fieldName: keyof T, fieldConfig: Field) => void;

export function getFieldChecker<T>(
	data: Record<string, unknown>,
	flag: ErrorFlagger<T>
) {
	return function checkField(
		fieldName: keyof T,
		{
			type,
			isRequired = true,
			canBeNull = false,
			allowedValues = [],
			followUpChecks = () => undefined
		}: Field
	) {
		if (allowedValues.length !== 0 && type !== "enum") {
			throw new Error("allowedValues only applies to enum fields");
		}

		if (allowedValues.length === 0 && type === "enum") {
			throw new Error("enum field has no allowed values");
		}

		let wasFoundInvalid = false;
		const findInvalid = (errMsg: string) => {
			wasFoundInvalid = true;
			flag(fieldName, errMsg);
		};

		if (typeof data !== "object") {
			throw new Error("Data was not an object");
		}

		if (data === null) {
			throw new Error("Data was null");
		}

		const value: unknown =
			fieldName in data ? (data as any)[fieldName] : undefined;

		if (value === undefined) {
			if (type === "absent") {
				return false;
			}
			if (isRequired) {
				findInvalid("missing");
			}
		} else if (value === null) {
			if (canBeNull === false) {
				findInvalid("null values are not allowed");
			}
		} else {
			switch (type) {
				case "string":
					if (typeof value !== "string") {
						findInvalid("not a string");
					}
					break;
				case "number":
					if (typeof value !== "number") {
						findInvalid("not a number");
					}
					break;
				case "url":
					if (typeof value !== "string") {
						findInvalid("not a string");
						break;
					}
					if (
						!/^(http(s):\/\/.)[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)$/.test(
							value
						)
					) {
						findInvalid("not an url");
					}
					break;
				case "datestring":
					if (typeof value !== "string" || !moment(value).isValid()) {
						flag(fieldName, "not a string representing a valid date");
					}
					break;
				case "completedatefieldselection":
					if (typeof value !== "object") {
						findInvalid("not an object");
						break;
					}
					const { year, month, date } = value as any;
					if (year === undefined || year !== "number") {
						findInvalid("year component was not a number");
					}
					if (month === undefined || month !== "number") {
						findInvalid("year component was not a number");
					}

					if (date === undefined || date !== "number") {
						findInvalid("year component was not a number");
					}

					if (!moment(value).isValid()) {
						flag(fieldName, "does not represent a valid date");
					}

					break;
				case "boolean":
					if (typeof value !== "boolean") {
						findInvalid("not a boolean");
					}
					break;
				case "1":
					if (value !== "1") {
						findInvalid("not the string '1'");
					}
					break;
				case "date":
					if (!(value instanceof Date)) {
						findInvalid("not a date");
					}
					break;
				case "function":
					if (typeof value !== "function") {
						findInvalid("not a function");
					}
					break;
				case "enum":
					if (typeof value !== "string" && typeof value !== "number") {
						findInvalid("Enum values must be strings or numbers");
						break;
					}
					if (!allowedValues.includes(value)) {
						findInvalid(`invalid value`);
					}
					break;
				case "keyvalue":

				// TODO:WV:20251028:The following type is a bodge to enable the validation of cardBillingDetails to pass.  It should ideally be removed and the other code refactored not to need it.
				// eslint-disable-next-line
				case "keyvaluewithboolean":
					if (typeof value !== "object") {
						findInvalid("not an object");
					} else {
						const err = [];

						const invalidKeys = Object.keys(value).filter(
							k => typeof k !== "string"
						);
						if (invalidKeys.length > 0) {
							err.push(
								`The following keys were not strings: {${invalidKeys.join(
									", "
								)}}`
							);
						}

						const keysOfInvalidValues = Object.entries(value)
							.filter(
								([k, v]) =>
									!(
										typeof v === "string" ||
										(type === "keyvaluewithboolean" && typeof v === "boolean")
									)
							)
							.map(([k]) => k);
						if (keysOfInvalidValues.length > 0) {
							err.push(
								`The values corresponding to the following keys were not strings${
									type === "keyvaluewithboolean" ? " or booleans" : ""
								}: {${keysOfInvalidValues.join(", ")}}`
							);
						}

						if (err.length) {
							findInvalid(JSON.stringify(err));
						}
					}
					break;
				case "array":
					if (!Array.isArray(value)) {
						findInvalid("not an array");
					}
					break;
				case "absent":
					if (value !== undefined) {
						findInvalid("should not be present");
					}
					break;
				default:
					throw new Error("Invalid field type");
			}
		}

		if (value !== undefined && !wasFoundInvalid) {
			followUpChecks({ findInvalid, value });
		}

		return !wasFoundInvalid;
	};
}
